/**
 * NativeAudioService - Native Web Audio API Service
 *
 * Replaces ToneService with native Web Audio API implementation.
 * Manages native AudioContext, voices, effects, and sequencer integration.
 */
import type { Voice } from './NativeVoiceFactory';
import { createNativeVoices } from './NativeVoiceFactory';
import { EffectsManager, type EffectInstance } from '../effects/EffectsManager';
import { SequencerService } from './SequencerService';
import type { InstrumentType } from '../../types/composition';
import type { InstrumentParameters } from '../../types/instruments';
import type { ADSRSettings } from './ADSREnvelope';
/**
 * NativeAudioService manages the native Web Audio API audio system
 */
/**
 * Voice configuration stored for recreation
 */
interface VoiceConfiguration {
    instrumentType: InstrumentType;
    adsrSettings?: ADSRSettings | undefined;
    parameters?: InstrumentParameters | undefined;
    parametersEnabled: boolean;
}
export class NativeAudioService {
    private audioContext: AudioContext | null = null;
    private voices: Voice[] = [];
    private effectsManager: EffectsManager;
    private masterGain: GainNode | null = null;
    private sequencer: SequencerService;
    private volumeLevels: number[] = [];
    private mutedStates: boolean[] = [];
    private effectsChains: EffectInstance[][] = [];
    private voiceConfigurations: VoiceConfiguration[] = [];
    private isInitialized: boolean = false;
    constructor() {
        this.effectsManager = new EffectsManager();
        this.sequencer = new SequencerService();
    }
    /**
     * Initialize the native audio service
     * Creates native AudioContext and initializes effects manager
     */
    async initialize(): Promise<void> {
        if (this.isInitialized) {
            console.warn('NativeAudioService already initialized');
            return;
        }
        try {
            // Check for AudioContext support
            const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
            if (!AudioContextClass) {
                throw new Error('AudioContext not supported in this browser');
            }
            // Create native AudioContext
            this.audioContext = new AudioContextClass();
            if (!this.audioContext) {
                throw new Error('Failed to create AudioContext');
            }
            console.log('[NativeAudioService] Created AudioContext:', {
                contextType: this.audioContext.constructor.name,
                state: this.audioContext.state,
                sampleRate: this.audioContext.sampleRate,
            });
            // Resume AudioContext if suspended (autoplay policy)
            if (this.audioContext.state === 'suspended') {
                await this.audioContext.resume();
                console.log('[NativeAudioService] AudioContext resumed, state:', this.audioContext.state);
            }
            // Create master gain node
            this.masterGain = this.audioContext.createGain();
            this.masterGain.gain.value = 1.0; // Default master volume
            this.masterGain.connect(this.audioContext.destination);
            // Initialize effects manager with native AudioContext
            await this.effectsManager.initialize(this.audioContext);
            this.isInitialized = true;
        }
        catch (error) {
            console.error('[NativeAudioService] Failed to initialize:', error);
            // Provide user-friendly error messages
            if (error instanceof Error) {
                if (error.message.includes('AudioContext not supported')) {
                    throw new Error('Your browser does not support Web Audio API. Please use a modern browser like Chrome, Firefox, Safari, or Edge.');
                }
                else if (error.message.includes('AudioWorklet')) {
                    throw new Error('Your browser does not support AudioWorklet (required for effects). Please update your browser to the latest version.');
                }
            }
            throw error;
        }
    }
    /**
     * Check if the service is ready to use
     */
    isReady(): boolean {
        return this.isInitialized && this.audioContext !== null && this.audioContext.state !== 'closed';
    }
    /**
     * Get the native AudioContext
     */
    getAudioContext(): AudioContext | null {
        return this.audioContext;
    }
    /**
     * Get the master gain node
     */
    getMasterGain(): GainNode | null {
        return this.masterGain;
    }
    /**
     * Get the effects manager
     */
    getEffectsManager(): EffectsManager {
        return this.effectsManager;
    }
    /**
     * Get the sequencer service
     */
    getSequencer(): SequencerService {
        return this.sequencer;
    }
    /**
     * Get current transport state
     */
    getState(): 'started' | 'paused' | 'stopped' {
        return this.sequencer.getState();
    }
    /**
     * Initialize the sequencer with current voices and grid state
     *
     * @param gridState - 2D array of active cells
     * @param noteState - 2D array of note pitches
     * @param steps - Number of steps in pattern
     * @param bpm - Beats per minute
     * @param onStepChange - Callback for step changes
     */
    initSequencer(gridState: boolean[][], noteState: (string | null)[][], steps: number, bpm: number, onStepChange?: (step: number) => void): void {
        this.sequencer.initSequencer(this.voices, gridState, noteState, steps, bpm, onStepChange);
    }
    /**
     * Update grid and note state on the sequencer without reinitializing.
     * Safe to call during playback.
     */
    updateGridState(gridState: boolean[][], noteState: (string | null)[][]): void {
        this.sequencer.updateGridState(gridState, noteState);
    }
    /**
     * Start playback
     */
    async play(): Promise<void> {
        if (!this.isInitialized) {
            await this.initialize();
        }
        await this.sequencer.start();
    }
    /**
     * Pause playback
     */
    pause(): void {
        this.sequencer.pause();
    }
    /**
     * Stop playback and reset position
     */
    stop(): void {
        this.sequencer.stop();
    }
    /**
     * Set BPM (beats per minute)
     *
     * @param bpm - New BPM value
     */
    setBpm(bpm: number): void {
        this.sequencer.setBpm(bpm);
    }
    /**
     * Set loop mode
     *
     * @param enabled - Whether looping is enabled
     */
    setLoop(enabled: boolean): void {
        this.sequencer.setLoop(enabled);
    }
    /**
     * Create voices with effects integration
     *
     * @param instrumentTypes - Array of instrument types for each voice
     * @param adsrSettings - Optional ADSR settings for each voice
     * @param instrumentParameters - Optional instrument parameters for each voice
     * @param parametersEnabled - Optional flags indicating if parameters are enabled for each voice
     */
    async createVoices(instrumentTypes: InstrumentType[], adsrSettings?: ADSRSettings[], instrumentParameters?: (InstrumentParameters | undefined)[], parametersEnabled?: boolean[]): Promise<void> {
        if (!this.audioContext) {
            throw new Error('AudioContext not initialized. Call initialize() first.');
        }
        if (!this.masterGain) {
            throw new Error('Master gain not initialized. Call initialize() first.');
        }
        console.log('[NativeAudioService] Creating voices:', {
            count: instrumentTypes.length,
            types: instrumentTypes,
        });
        try {
            // Dispose old voices first
            this.disposeVoices();
            // Store voice configurations for potential recreation
            this.voiceConfigurations = instrumentTypes.map((instrumentType, index) => ({
                instrumentType,
                adsrSettings: adsrSettings?.[index],
                parameters: instrumentParameters?.[index],
                parametersEnabled: parametersEnabled?.[index] ?? false,
            }));
            // Preserve existing volume levels, muted states, and effects chains
            // Only initialize new entries if the array is growing
            const oldVolumeLevels = this.volumeLevels;
            const oldMutedStates = this.mutedStates;
            const oldEffectsChains = this.effectsChains;
            this.volumeLevels = new Array(instrumentTypes.length).fill(1.0).map((_, i) => oldVolumeLevels[i] ?? 1.0);
            this.mutedStates = new Array(instrumentTypes.length).fill(false).map((_, i) => oldMutedStates[i] ?? false);
            this.effectsChains = new Array(instrumentTypes.length).fill([]).map((_, i) => oldEffectsChains[i] ?? []);
            // Create effects nodes for each voice using EffectsManager
            const effectsNodes: (AudioWorkletNode | null)[] = [];
            for (let i = 0; i < instrumentTypes.length; i++) {
                try {
                    // Create or get AudioWorkletNode for this row
                    const effectsNode = await this.effectsManager.createWorkletNode(i);
                    effectsNodes.push(effectsNode);
                }
                catch (error) {
                    console.error(`[NativeAudioService] Failed to create effects node for voice ${i}:`, error);
                    // Push null to allow voice creation without effects
                    effectsNodes.push(null);
                }
            }
            // Create voices using NativeVoiceFactory
            // Pass effects nodes and master gain as destination so voices connect through them
            this.voices = createNativeVoices(this.audioContext, instrumentTypes, adsrSettings, instrumentParameters, parametersEnabled, effectsNodes, this.masterGain // Pass our master gain as destination
            );
            // Reapply effects chains to the new voices
            for (let i = 0; i < this.effectsChains.length; i++) {
                const chain = this.effectsChains[i];
                if (chain && chain.length > 0) {
                    console.log(`[NativeAudioService] Reapplying effects chain for voice ${i}:`, chain);
                    try {
                        await this.updateEffectsChain(i, chain);
                    }
                    catch (error) {
                        console.error(`[NativeAudioService] Failed to reapply effects chain for voice ${i}:`, error);
                    }
                }
            }
        }
        catch (error) {
            console.error('[NativeAudioService] Failed to create voices:', error);
            // Attempt recovery: create voices without effects
            try {
                // Create voices without effects nodes
                this.voices = createNativeVoices(this.audioContext, instrumentTypes, adsrSettings, instrumentParameters, parametersEnabled, [], // No effects
                this.masterGain // Pass our master gain as destination
                );
            }
            catch (recoveryError) {
                console.error('[NativeAudioService] Failed to recover:', recoveryError);
                throw new Error('Failed to create audio voices. Please check browser compatibility.');
            }
        }
    }
    /**
     * Dispose of the audio service and clean up resources
     */
    dispose(): void {
        // Dispose sequencer
        if (this.sequencer) {
            this.sequencer.dispose();
        }
        // Dispose all voices
        this.disposeVoices();
        // Dispose effects manager
        if (this.effectsManager) {
            this.effectsManager.dispose();
        }
        // Disconnect and close master gain
        if (this.masterGain) {
            try {
                this.masterGain.disconnect();
            }
            catch (e) {
                // Ignore errors from already-disconnected nodes
            }
            this.masterGain = null;
        }
        // Close audio context
        if (this.audioContext) {
            try {
                this.audioContext.close();
            }
            catch (e) {
                console.error('[NativeAudioService] Error closing AudioContext:', e);
            }
            this.audioContext = null;
        }
        // Clear state
        this.volumeLevels = [];
        this.mutedStates = [];
        this.effectsChains = [];
        this.voiceConfigurations = [];
        this.isInitialized = false;
    }
    /**
     * Dispose all voices
     * @private
     */
    private disposeVoices(): void {
        this.voices.forEach((voice, index) => {
            try {
                voice.dispose();
            }
            catch (error) {
                console.error(`[NativeAudioService] Error disposing voice ${index}:`, error);
            }
        });
        this.voices = [];
    }
    /**
     * Set volume for a specific voice
     *
     * @param row - Voice index (0-based)
     * @param volume - Volume level (0.0 - 1.0)
     */
    setVolume(row: number, volume: number): void {
        // Validate row index — silently skip if voices aren't ready yet
        // (can happen when volume sync fires during async voice recreation)
        if (row < 0 || row >= this.voices.length) {
            console.error(`[NativeAudioService] Invalid row index for setVolume: ${row}`);
            return;
        }
        // Clamp volume to valid range (0.0 - 1.0)
        const clampedVolume = Math.max(0.0, Math.min(1.0, volume));
        // Store the volume level for this voice
        this.volumeLevels[row] = clampedVolume;
        // Get the voice
        const voice = this.voices[row];
        if (!voice || !voice.gainNode) {
            console.error(`[NativeAudioService] Voice ${row} not found or has no gain node`);
            return;
        }
        // Apply volume change with smooth ramp to avoid clicks
        // Use 10ms ramp time as specified in requirements (6.4)
        const currentTime = this.audioContext?.currentTime ?? 0;
        const rampTime = 0.01; // 10 milliseconds
        try {
            // If muted, don't change the gain (keep it at 0)
            if (this.mutedStates[row]) {
                return;
            }
            // Cancel any scheduled parameter changes
            voice.gainNode.gain.cancelScheduledValues(currentTime);
            // Set current value and ramp to new value
            voice.gainNode.gain.setValueAtTime(voice.gainNode.gain.value, currentTime);
            voice.gainNode.gain.linearRampToValueAtTime(clampedVolume, currentTime + rampTime);
        }
        catch (error) {
            console.error(`[NativeAudioService] Error setting volume for voice ${row}:`, error);
        }
    }
    /**
     * Set mute state for a specific voice
     *
     * @param row - Voice index (0-based)
     * @param muted - True to mute, false to unmute
     */
    setMute(row: number, muted: boolean): void {
        // Validate row index — silently skip if voices aren't ready yet
        // (can happen when mute sync fires during async voice recreation)
        if (row < 0 || row >= this.voices.length) {
            console.error(`[NativeAudioService] Invalid row index for setMute: ${row}`);
            return;
        }
        // Get the voice
        const voice = this.voices[row];
        if (!voice || !voice.gainNode) {
            console.error(`[NativeAudioService] Voice ${row} not found or has no gain node`);
            return;
        }
        // Store the muted state
        this.mutedStates[row] = muted;
        // Apply mute/unmute with smooth ramp to avoid clicks
        const currentTime = this.audioContext?.currentTime ?? 0;
        const rampTime = 0.01; // 10 milliseconds
        try {
            // Cancel any scheduled parameter changes
            voice.gainNode.gain.cancelScheduledValues(currentTime);
            // Set current value
            voice.gainNode.gain.setValueAtTime(voice.gainNode.gain.value, currentTime);
            if (muted) {
                // Mute: ramp to 0
                voice.gainNode.gain.linearRampToValueAtTime(0, currentTime + rampTime);
            }
            else {
                // Unmute: restore previous volume level
                const restoredVolume = this.volumeLevels[row] ?? 1.0;
                voice.gainNode.gain.linearRampToValueAtTime(restoredVolume, currentTime + rampTime);
            }
        }
        catch (error) {
            console.error(`[NativeAudioService] Error setting mute for voice ${row}:`, error);
        }
    }
    /**
     * Get volume level for a specific voice
     *
     * @param row - Voice index (0-based)
     * @returns Volume level (0.0 - 1.0) or undefined if invalid row
     */
    getVolume(row: number): number | undefined {
        if (row < 0 || row >= this.volumeLevels.length) {
            return undefined;
        }
        return this.volumeLevels[row];
    }
    /**
     * Get mute state for a specific voice
     *
     * @param row - Voice index (0-based)
     * @returns True if muted, false if not muted, undefined if invalid row
     */
    getMute(row: number): boolean | undefined {
        if (row < 0 || row >= this.mutedStates.length) {
            return undefined;
        }
        return this.mutedStates[row];
    }
    /**
     * Update voice parameters for a specific voice
     *
     * @param row - Voice index (0-based)
     * @param parameters - New instrument parameters
     */
    updateVoiceParameters(row: number, parameters: InstrumentParameters): void {
        // Validate row index
        if (row < 0 || row >= this.voices.length) {
            console.error(`[NativeAudioService] Invalid row index: ${row}`);
            return;
        }
        const voice = this.voices[row];
        if (!voice) {
            console.error(`[NativeAudioService] Voice ${row} not found`);
            return;
        }
        try {
            voice.updateParameters(parameters);
        }
        catch (error) {
            console.error(`[NativeAudioService] Error updating parameters for voice ${row}:`, error);
        }
    }
    /**
     * Update the effects chain for a specific voice
     * Creates or gets AudioWorkletNode for the row and updates the effects chain
     * Recreates the voice if the effects node changes
     *
     * @param row - Voice index (0-based)
     * @param effects - Array of effect instances for the chain
     */
    async updateEffectsChain(row: number, effects: EffectInstance[]): Promise<void> {
        // Validate row index - silently skip if voice doesn't exist yet
        // This can happen when effects are updated before voices are created
        if (row < 0 || row >= this.voices.length) {
            return;
        }
        if (!this.audioContext) {
            console.error('[NativeAudioService] AudioContext not initialized');
            throw new Error('AudioContext not initialized. Call initialize() first.');
        }
        console.log(`[NativeAudioService] Updating effects chain for row ${row}:`, effects);
        try {
            // Get current effects node for this row
            const currentEffectsNode = this.voices[row]?.effectsNode || null;
            // Create or get AudioWorkletNode for this row
            let newEffectsNode: AudioWorkletNode | null = null;
            if (effects.length > 0) {
                // Need effects node - create or get existing one
                newEffectsNode = this.effectsManager.getWorkletNode(row) || null;
                if (!newEffectsNode) {
                    newEffectsNode = await this.effectsManager.createWorkletNode(row);
                }
            }
            else {
                // No effects - dispose existing worklet node if present
                const existingNode = this.effectsManager.getWorkletNode(row);
                if (existingNode) {
                }
            }
            // Update effects chain in EffectsManager
            await this.effectsManager.updateEffectsChain(row, effects);
            // Store the updated effects chain
            this.effectsChains[row] = effects;
            // Check if effects node changed (different node or null vs non-null)
            const effectsNodeChanged = currentEffectsNode !== newEffectsNode;
            if (effectsNodeChanged) {
                // Recreate the voice with the new effects node
                await this.recreateVoice(row, newEffectsNode);
            }
            else {
            }
        }
        catch (error) {
            console.error(`[NativeAudioService] Failed to update effects chain for row ${row}:`, error);
            throw error;
        }
    }
    /**
     * Recreate a voice with a new effects node
     * Preserves voice configuration, volume, and mute state
     *
     * @param row - Voice index (0-based)
     * @param effectsNode - New effects node to use (or null for no effects)
     * @private
     */
    private async recreateVoice(row: number, effectsNode: AudioWorkletNode | null): Promise<void> {
        if (!this.audioContext || !this.masterGain) {
            throw new Error('AudioContext or master gain not initialized');
        }
        console.log(`[NativeAudioService] Recreating voice ${row} with ${effectsNode ? 'effects' : 'no effects'}`);
        try {
            // Get voice configuration
            const config = this.voiceConfigurations[row];
            if (!config) {
                throw new Error(`No configuration found for voice ${row}`);
            }
            // Store current volume and mute state
            const currentVolume = this.volumeLevels[row] ?? 1.0;
            const currentMuted = this.mutedStates[row] ?? false;
            // Dispose old voice
            const oldVoice = this.voices[row];
            if (oldVoice) {
                try {
                    oldVoice.dispose();
                }
                catch (error) {
                    console.error(`[NativeAudioService] Error disposing old voice ${row}:`, error);
                }
            }
            // Create new voice with updated effects node
            const newVoices = createNativeVoices(this.audioContext, [config.instrumentType], config.adsrSettings ? [config.adsrSettings] : undefined, config.parameters ? [config.parameters] : undefined, [config.parametersEnabled], [effectsNode], this.masterGain);
            if (newVoices.length === 0) {
                throw new Error(`Failed to create voice ${row}`);
            }
            // Replace the voice in the array
            const newVoice = newVoices[0];
            if (!newVoice) {
                throw new Error(`Failed to get voice from created voices array for row ${row}`);
            }
            this.voices[row] = newVoice;
            // Restore volume and mute state
            this.volumeLevels[row] = currentVolume;
            this.mutedStates[row] = currentMuted;
            // Apply the volume/mute state to the new voice
            if (currentMuted) {
                // If muted, set gain to 0
                newVoice.gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
            }
            else {
                // If not muted, set to current volume
                newVoice.gainNode.gain.setValueAtTime(currentVolume, this.audioContext.currentTime);
            }
        }
        catch (error) {
            console.error(`[NativeAudioService] Failed to recreate voice ${row}:`, error);
            throw error;
        }
    }
    /**
     * Get the effects chain for a specific voice
     *
     * @param row - Voice index (0-based)
     * @returns Array of effect instances or empty array if invalid row
     */
    getEffectsChain(row: number): EffectInstance[] {
        if (row < 0 || row >= this.effectsChains.length) {
            console.warn(`[NativeAudioService] Invalid row index for getEffectsChain: ${row}`);
            return [];
        }
        return this.effectsChains[row] || [];
    }
    /**
     * Update a parameter for a specific effect
     * Delegates to EffectsManager and updates local effects chain
     *
     * @param row - Voice index (0-based)
     * @param effectId - Unique identifier of the effect
     * @param parameterId - Parameter identifier (e.g., 'sampleRate', 'bitDepth')
     * @param value - New parameter value
     */
    updateEffectParameter(row: number, effectId: string, parameterId: string, value: number): void {
        // Validate row index
        if (row < 0 || row >= this.effectsChains.length) {
            console.error(`[NativeAudioService] Invalid row index: ${row}`);
            return;
        }
        try {
            // Get current effects chain
            const currentChain = this.effectsChains[row] || [];
            // Delegate to EffectsManager to update parameter
            const updatedChain = this.effectsManager.updateEffectParameter(row, effectId, parameterId, value, currentChain);
            // Update local effects chain
            this.effectsChains[row] = updatedChain;
        }
        catch (error) {
            console.error(`[NativeAudioService] Failed to update effect parameter for row ${row}:`, error);
            throw error;
        }
    }
    /**
     * Toggle bypass state for a specific effect
     * When bypassed, the effect is skipped in the processing chain
     * Delegates to EffectsManager and updates local effects chain
     *
     * @param row - Voice index (0-based)
     * @param effectId - Unique identifier of the effect
     * @param bypassed - True to bypass, false to enable
     */
    toggleEffectBypass(row: number, effectId: string, bypassed: boolean): void {
        // Validate row index
        if (row < 0 || row >= this.effectsChains.length) {
            console.error(`[NativeAudioService] Invalid row index: ${row}`);
            return;
        }
        try {
            // Get current effects chain
            const currentChain = this.effectsChains[row] || [];
            // Delegate to EffectsManager to toggle bypass
            const updatedChain = this.effectsManager.toggleEffectBypass(row, effectId, bypassed, currentChain);
            // Update local effects chain
            this.effectsChains[row] = updatedChain;
        }
        catch (error) {
            console.error(`[NativeAudioService] Failed to toggle effect bypass for row ${row}:`, error);
            throw error;
        }
    }
    /**
     * Remove an effect from the effects chain
     * Delegates to EffectsManager and updates local effects chain
     *
     * @param row - Voice index (0-based)
     * @param effectId - Unique identifier of the effect to remove
     */
    async removeEffect(row: number, effectId: string): Promise<void> {
        // Validate row index
        if (row < 0 || row >= this.effectsChains.length) {
            console.error(`[NativeAudioService] Invalid row index: ${row}`);
            throw new Error(`Invalid row index: ${row}`);
        }
        try {
            // Get current effects chain
            const currentChain = this.effectsChains[row] || [];
            // Delegate to EffectsManager to remove effect
            const updatedChain = await this.effectsManager.removeEffect(row, effectId, currentChain);
            // Update local effects chain
            this.effectsChains[row] = updatedChain;
        }
        catch (error) {
            console.error(`[NativeAudioService] Failed to remove effect from row ${row}:`, error);
            throw error;
        }
    }
    /**
     * Add an effect to the effects chain at a specific slot
     * Delegates to EffectsManager and updates local effects chain
     *
     * @param row - Voice index (0-based)
     * @param slot - Slot position in the effects chain (0-based)
     * @param type - Effect type identifier (e.g., 'bitcrusher')
     */
    async addEffect(row: number, slot: number, type: string): Promise<void> {
        // Validate row index
        if (row < 0 || row >= this.effectsChains.length) {
            console.error(`[NativeAudioService] Invalid row index: ${row}`);
            throw new Error(`Invalid row index: ${row}`);
        }
        try {
            // Get current effects chain
            const currentChain = this.effectsChains[row] || [];
            // Delegate to EffectsManager to add effect
            // Cast type to EffectType (EffectsManager will validate)
            const updatedChain = await this.effectsManager.addEffect(row, slot, type as any, // Type will be validated by EffectsManager
            currentChain);
            // Update local effects chain
            this.effectsChains[row] = updatedChain;
        }
        catch (error) {
            console.error(`[NativeAudioService] Failed to add effect to row ${row}:`, error);
            throw error;
        }
    }
}
// Export singleton instance
export const nativeAudioService = new NativeAudioService();
